import { ReleaseNote, ReleaseNotesState } from '@/types/releaseNotes'

// Import release notes from generated JSON file
// This file is generated by `npm run release-notes:build` from markdown files
// Using synchronous import - the file will be included in the bundle during build
import releaseNotesData from './release-notes-data.json'

const STORAGE_KEY = 'datacentral_release_notes'

/** Fired on `window` after the read state changes, so the topbar badge and the panel stay in step. */
export const RELEASE_NOTES_CHANGED = 'datacentral:release-notes-changed'

// Fallback - empty array since JSON should always be generated during build
const fallbackReleaseNotes: ReleaseNote[] = []

// Cast imported JSON data to ReleaseNote type
const typedReleaseNotesData = releaseNotesData as ReleaseNote[]

// Helper to get all release notes
const getAllNotes = (): ReleaseNote[] => {
    return typedReleaseNotesData.length > 0
        ? typedReleaseNotesData
        : fallbackReleaseNotes
}

/**
 * Clear invalid read notes that don't exist in current release notes
 * This prevents stale data from causing incorrect unread counts
 */
const cleanupInvalidReadNotes = (
    state: ReleaseNotesState
): ReleaseNotesState => {
    const validIds = new Set(getAllNotes().map((note) => note.id))
    const cleanedReadNotes = state.readNotes.filter((id) => validIds.has(id))

    // Only update if something was cleaned
    if (cleanedReadNotes.length !== state.readNotes.length) {
        return { ...state, readNotes: cleanedReadNotes }
    }
    return state
}

export const getReleaseNotesState = (): ReleaseNotesState => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
            const state = JSON.parse(stored) as ReleaseNotesState
            // Auto-cleanup invalid IDs
            const cleanedState = cleanupInvalidReadNotes(state)
            if (cleanedState !== state) {
                saveReleaseNotesState(cleanedState)
            }
            return cleanedState
        }
    } catch (error) {
        console.error('Error reading release notes state:', error)
    }

    return {
        readNotes: [],
        lastChecked: new Date().toISOString(),
    }
}

const saveReleaseNotesState = (state: ReleaseNotesState): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (error) {
        console.error('Error saving release notes state:', error)
    }
}

export const markNoteAsRead = (noteId: string): void => {
    const state = getReleaseNotesState()
    if (!state.readNotes.includes(noteId)) {
        state.readNotes.push(noteId)
        saveReleaseNotesState(state)
        window.dispatchEvent(new Event(RELEASE_NOTES_CHANGED))
    }
}

/** Marks every note as read; one RELEASE_NOTES_CHANGED event for the lot. */
export const markAllNotesAsRead = (): void => {
    const state = getReleaseNotesState()
    const unread = getAllNotes()
        .map((note) => note.id)
        .filter((id) => !state.readNotes.includes(id))
    if (unread.length === 0) return
    state.readNotes.push(...unread)
    saveReleaseNotesState(state)
    window.dispatchEvent(new Event(RELEASE_NOTES_CHANGED))
}

export const getAllReleaseNotes = (): ReleaseNote[] => {
    return getAllNotes().sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )
}

/**
 * Get current app version from build
 */
export const getCurrentAppVersion = (): string => {
    return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'
}

/**
 * Get the last seen app version from localStorage
 */
const LAST_SEEN_VERSION_KEY = 'datacentral_last_seen_version'

const getLastSeenVersion = (): string | null => {
    return localStorage.getItem(LAST_SEEN_VERSION_KEY)
}

const setLastSeenVersion = (version: string): void => {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, version)
}

/**
 * Compare two semver versions
 * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
const compareVersions = (v1: string, v2: string): number => {
    const normalize = (v: string) => v.replace(/^v/, '').split('.').map(Number)
    const parts1 = normalize(v1)
    const parts2 = normalize(v2)

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
        const p1 = parts1[i] || 0
        const p2 = parts2[i] || 0
        if (p1 > p2) return 1
        if (p1 < p2) return -1
    }
    return 0
}

/**
 * Check if there's a new version since last seen
 */
export const hasNewVersion = (): boolean => {
    const currentVersion = getCurrentAppVersion()
    const lastSeenVersion = getLastSeenVersion()

    if (!lastSeenVersion) {
        // First time user - don't show modal, just save current version
        setLastSeenVersion(currentVersion)
        return false
    }

    return compareVersions(currentVersion, lastSeenVersion) > 0
}

/**
 * Get release notes for versions newer than last seen
 */
export const getNewVersionNotes = (): ReleaseNote[] => {
    const lastSeenVersion = getLastSeenVersion()

    if (!lastSeenVersion) {
        return []
    }

    return getAllNotes().filter((note) => {
        return compareVersions(note.version, lastSeenVersion) > 0
    })
}

/**
 * Mark current version as seen
 */
export const markCurrentVersionAsSeen = (): void => {
    const currentVersion = getCurrentAppVersion()
    setLastSeenVersion(currentVersion)
}
